import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../services/api';
import PlatformConfirmModal from '../components/PlatformConfirmModal';
import { getApiUrl } from '../config/runtimeConfig';

import { getAccessToken, clearAuthStorage } from '../services/api';
import Loading from '../components/Loading';
const GAME_ICONS = { whot: '🎴' };
const GAME_NAMES = { whot: 'WHOT' };

const SOCKET_URL = getApiUrl().replace(/\/api$/, '');

export default function RoomWaitingPage() {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const location = useLocation();
  const token = getAccessToken();
  const socketRef = useRef(null);

  // Room data from navigation state (fallback) or fetched from API
  const [room, setRoom] = useState(() => location.state || null);
  const [players, setPlayers] = useState([]);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!location.state);
  const [countdown, setCountdown] = useState(null);
  const [inviteFriends, setInviteFriends] = useState([]);
  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [inviteBusy, setInviteBusy] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const fetchRoom = useCallback(async () => {
    try {
      const res = await api.get(`/rooms/${roomId}`);
      if (res.data.room) {
        setRoom(res.data.room);
        setPlayers(res.data.room.players || []);
      }
    } catch (err) {
      setError('Failed to load room');
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  const getCurrentUserId = () => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload.userId ?? payload.id ?? payload.sub;
    } catch (_) { return null; }
  };

  const isHost = room?.hostid != null && String(room.hostid) === String(getCurrentUserId());

  const loadInviteFriends = useCallback(async () => {
    if (!roomId || !isHost || room?.status !== 'waiting') return;
    try {
      const res = await api.rooms.getRoomInvitations(roomId);
      setInviteFriends(res.data.invitations || []);
    } catch (_) {}
  }, [roomId, isHost, room?.status]);

  useEffect(() => { loadInviteFriends(); }, [loadInviteFriends]);

  async function inviteFriend(friend) {
    if (!isHost || !roomId || inviteBusy) return;
    setInviteBusy(String(friend.friendUserId));
    setInviteMessage('');
    try {
      await api.rooms.inviteFriend(roomId, friend.friendUserId);
      setInviteMessage(`Invitation sent to ${friend.username}`);
      await loadInviteFriends();
    } catch (err) {
      setInviteMessage(err.response?.data?.error || 'Unable to send invitation');
    } finally { setInviteBusy(''); }
  }

  async function cancelInvitation(invitationId) {
    setInviteBusy(String(invitationId));
    try {
      await api.rooms.cancelInvitation(invitationId);
      await loadInviteFriends();
    } catch (err) {
      setInviteMessage(err.response?.data?.error || 'Unable to cancel invitation');
    } finally { setInviteBusy(''); }
  }

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (!location.state) {
      fetchRoom();
    } else if (location.state.players) {
      setPlayers(location.state.players);
    }

    // Socket connection for real-time updates
    socketRef.current = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    const socket = socketRef.current;

    socket.on('room:state', (data) => {
      if (data.roomId && String(data.roomId) === String(roomId)) {
        setPlayers(data.players || []);
        setRoom((prev) => prev ? { ...prev, status: data.status, players: data.players || prev.players } : prev);
      }
    });

    socket.on('room:playerJoined', (data) => {
      setPlayers((prev) => {
        const exists = prev.some((p) => p.userId === data.userId);
        if (exists) return prev;
        return [...prev, { userId: data.userId, username: data.username || `Player ${data.userId}` }];
      });
    });

    socket.on('room:playerLeft', (data) => {
      setPlayers((prev) => prev.filter((p) => p.userId !== data.userId));
    });

    socket.on('room:invitationAccepted', (data) => {
      if (String(data.roomId) !== String(roomId)) return;
      setInviteMessage(`${data.username || 'Your friend'} joined the room`);
      fetchRoom();
      loadInviteFriends();
    });

    socket.on('room:invitationDeclined', (data) => {
      if (String(data.roomId) !== String(roomId)) return;
      loadInviteFriends();
    });

    socket.on('room:fullAutoStart', (data) => {
      navigateToGame(data.gameType, data.roomId);
    });

    socket.on('room:matched', (data) => {
      setPlayers(data.players || []);
      setCountdown(5);
    });

    socket.on('room:countdown', (data) => {
      if (data.roomId && String(data.roomId) !== String(roomId)) return;
      const value = Number(data.count);
      setCountdown(Number.isFinite(value) ? value : null);
    });

    socket.on('room:started', (data) => {
      navigateToGame(room?.gametype || data.gameType, data.roomId);
    });

    socket.on('room:cancelled', () => {
      alert('The host has cancelled this room.');
      navigate('/rooms');
    });

    // Join the socket room
    socket.emit('room:join', { roomCode: room?.code || roomId });

    // Authoritative polling fallback: never leave a host/client stuck on
    // 'Waiting' if a websocket event was missed during reconnect.
    const syncTimer = setInterval(async () => {
      try {
        const res = await api.get(`/rooms/${roomId}`);
        const current = res.data.room;
        if (!current) return;
        setRoom(current);
        setPlayers(current.players || []);
        if (current.status === 'in_progress') {
          navigateToGame(current.gametype, current.id);
        } else if (current.status !== 'countdown') {
          setCountdown(null);
        }
      } catch (_) {}
    }, 1000);

    return () => {
      clearInterval(syncTimer);
      socket.emit('room:leave', { roomCode: room?.code || roomId });
      socket.disconnect();
    };
  }, [token, navigate, roomId, location.state, fetchRoom, room?.gametype, loadInviteFriends]);

  function navigateToGame(gameType, id) {
    navigate(`/play/${gameType}/${id}`, {
      state: { matchSource: 'room', matchId: id },
    });
  }

  async function handleCancel() {
    try {
      await api.rooms.cancel(roomId);
      navigate('/rooms');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to cancel room');
    }
  }

  function handleCancelClick() {
    setShowCancelConfirm(true);
  }

  function handleCopyCode() {
    if (!room?.code) return;
    navigator.clipboard.writeText(room.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function formatFee(fee) {
    const f = parseFloat(fee);
    if (!f || f <= 0) return 'Free';
    return `₦${f.toLocaleString()}`;
  }

  if (!token) return null;
  if (loading) {
    return (
      <Loading variant="section" label="" />
    );
  }
  if (error) {
    return (
      <div style={{ textAlign: 'center', paddingTop: '60px', color: '#ff4444' }}>
        <p>{error}</p>
        <button onClick={() => navigate('/rooms')} style={{
          marginTop: '16px', padding: '10px 20px', background: '#00d4aa', color: '#000',
          border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600,
        }}>
          Back to Rooms
        </button>
      </div>
    );
  }
  if (!room) {
    return (
      <div style={{ textAlign: 'center', paddingTop: '60px', color: '#888' }}>
        <p>Room not found</p>
        <button onClick={() => navigate('/rooms')} style={{
          marginTop: '16px', padding: '10px 20px', background: '#00d4aa', color: '#000',
          border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600,
        }}>
          Back to Rooms
        </button>
      </div>
    );
  }

  const styles = {
    container: {
      padding: '16px',
      maxWidth: '520px',
      margin: '0 auto',
      color: '#fff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '24px',
    },
    backBtn: {
      background: 'transparent',
      border: 'none',
      color: '#00d4aa',
      fontSize: '20px',
      cursor: 'pointer',
      padding: '8px 12px 8px 0',
    },
    title: {
      fontSize: '1.3rem',
      margin: 0,
      color: '#fff',
      flex: 1,
    },
    card: {
      background: '#1a1a2e',
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '20px',
      textAlign: 'center',
    },
    gameLabel: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#fff',
      marginBottom: '8px',
    },
    roomMeta: {
      color: '#888',
      fontSize: '14px',
      marginBottom: '16px',
    },
    codeSection: {
      marginBottom: '20px',
    },
    codeLabel: {
      color: '#aaa',
      fontSize: '12px',
      marginBottom: '8px',
    },
    codeDisplay: {
      fontSize: '36px',
      fontWeight: 800,
      letterSpacing: '8px',
      color: '#00d4aa',
      background: '#0d0d1a',
      padding: '16px',
      borderRadius: '12px',
      fontFamily: 'monospace',
    },
    copyBtn: {
      marginTop: '12px',
      padding: '10px 24px',
      background: '#00d4aa',
      color: '#000',
      border: 'none',
      borderRadius: '8px',
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: '14px',
    },
    playersSection: {
      marginBottom: '20px',
    },
    playersLabel: {
      color: '#aaa',
      fontSize: '13px',
      marginBottom: '12px',
    },
    playerList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      justifyContent: 'center',
      marginBottom: '16px',
    },
    playerChip: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      background: '#0d0d1a',
      padding: '10px 16px',
      borderRadius: '10px',
      border: '1px solid #2a2a4a',
    },
    playerAvatar: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #00d4aa, #00b894)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: '14px',
      color: '#000',
    },
    playerName: {
      color: '#fff',
      fontSize: '14px',
    },
    playerCount: {
      color: '#888',
      fontSize: '13px',
      marginTop: '8px',
    },
    waitingSpinner: {
      display: 'inline-block',
      width: '20px',
      height: '20px',
      border: '3px solid #333',
      borderTopColor: '#00d4aa',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
      marginRight: '8px',
      verticalAlign: 'middle',
    },
    waitingText: {
      color: '#888',
      fontSize: '14px',
    },
    inviteBtn: { width: '100%', padding: '13px', background: '#ff9800', color: '#111', border: 'none', borderRadius: '9px', cursor: 'pointer', fontSize: '14px', fontWeight: 800 },
    inviteMessage: { padding: '9px 10px', marginBottom: 10, borderRadius: 8, background: 'rgba(255,152,0,.1)', color: '#ffb74d', fontSize: 12 },
    friendRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 0', borderBottom: '1px solid #28283a' },
    friendIdentity: { display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 },
    friendAvatar: { width: 34, height: 34, borderRadius: '50%', display: 'grid', placeItems: 'center', background: '#ff9800', color: '#111', fontWeight: 800, flex: '0 0 auto' },
    inviteSmallBtn: { padding: '8px 12px', border: 0, borderRadius: 7, background: '#ff9800', color: '#111', fontWeight: 800, cursor: 'pointer', fontSize: 12 },
    pendingBtn: { padding: '8px 10px', border: '1px solid #555', borderRadius: 7, background: 'transparent', color: '#aaa', fontWeight: 700, cursor: 'pointer', fontSize: 11 },
    cancelBtn: {
      width: '100%',
      padding: '12px',
      background: 'transparent',
      border: '1px solid #ff4444',
      color: '#ff4444',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 600,
      marginTop: '16px',
    },
    publicBadge: {
      display: 'inline-block',
      padding: '4px 12px',
      background: 'rgba(0,212,170,0.15)',
      color: '#00d4aa',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: 600,
      marginBottom: '16px',
    },
  };

  return (
    <>
      {showCancelConfirm && (
        <PlatformConfirmModal
          title="Cancel Room?"
          message={"Are you sure you want to cancel this room?\nYour room will be cancelled and you will leave the room."}
          cancelLabel="Keep Room"
          confirmLabel="Cancel Room"
          onCancel={() => setShowCancelConfirm(false)}
          onConfirm={() => { setShowCancelConfirm(false); handleCancel(); }}
        />
      )}
      <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate('/rooms')} style={styles.backBtn}>← Back</button>
        <h1 style={styles.title}>Waiting Room</h1>
      </div>

      {/* Room Info Card */}
      <div style={styles.card}>
        <div style={{ fontSize: '48px', marginBottom: '8px' }}>
          {GAME_ICONS[room.gametype] || '🎮'}
        </div>
        <div style={styles.gameLabel}>
          {GAME_ICONS[room.gametype]} {GAME_NAMES[room.gametype] || room.gametype}
        </div>
        <div style={styles.roomMeta}>
          {room.playercount}P • {formatFee(room.entryfee)}
        </div>
        {countdown !== null && countdown > 0 && room.status === 'countdown' && (
          <div style={{margin:'12px 0 18px',padding:'14px',borderRadius:14,background:'#0d0d1a',border:'1px solid #2a2a4a'}}>
            <div style={{fontSize:12,color:'#888',marginBottom:4}}>GAME STARTING</div>
            <div style={{fontSize:42,fontWeight:900,color:'#00d4aa',lineHeight:1}}>{countdown}</div>
          </div>
        )}

        {/* Room Code (private only) */}
        {room.code && (
          <div style={styles.codeSection}>
            <div style={styles.codeLabel}>Share this code with friends:</div>
            <div style={styles.codeDisplay}>{room.code}</div>
            <button
              style={{ ...styles.copyBtn, background: copied ? '#00b894' : '#00d4aa' }}
              onClick={handleCopyCode}
            >
              {copied ? '✓ Copied!' : '📋 Copy Code'}
            </button>
          </div>
        )}

        {!room.code && (
          <div style={styles.publicBadge}>🌐 Public Room — Anyone can join</div>
        )}
      </div>

      {/* Players */}
      <div style={{ ...styles.card, textAlign: 'left' }}>
        <div style={styles.playersLabel}>
          Players ({players.length}/{room.playercount})
        </div>

        <div style={styles.playerList}>
          {Array.from({ length: room.playercount }).map((_, i) => {
            const player = players[i];
            if (player) {
              return (
                <div key={player.userId || i} style={styles.playerChip}>
                  <div style={styles.playerAvatar}>
                    {(player.username || '?')[0].toUpperCase()}
                  </div>
                  <span style={styles.playerName}>{player.username || 'Unknown'}</span>
                </div>
              );
            }
            return (
              <div key={`empty-${i}`} style={{
                ...styles.playerChip,
                border: '1px dashed #333',
                opacity: 0.5,
              }}>
                <div style={{
                  ...styles.playerAvatar,
                  background: '#333',
                }}>?</div>
                <span style={{ color: '#666', fontSize: '14px' }}>Waiting...</span>
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          {players.length < room.playercount ? (
            <span style={styles.waitingText}>
              Waiting for {room.playercount - players.length} more player(s)...
            </span>
          ) : (
            <span style={{ color: '#00d4aa', fontWeight: 600 }}>
              ✓ Room full — starting soon!
            </span>
          )}
        </div>
      </div>

      {isHost && room.status === 'waiting' && players.length < Number(room.playercount) && (
        <div style={{ ...styles.card, textAlign: 'left' }}>
          <button onClick={() => { setShowInvitePanel(v => !v); setInviteMessage(''); loadInviteFriends(); }} style={styles.inviteBtn}>
            👥 {showInvitePanel ? 'Close' : 'Invite Friends'}
          </button>
          {showInvitePanel && (
            <div style={{ marginTop: 14 }}>
              {inviteMessage && <div style={styles.inviteMessage}>{inviteMessage}</div>}
              {inviteFriends.length === 0 ? (
                <div style={{ color: '#888', fontSize: 13 }}>No friends available to invite.</div>
              ) : inviteFriends.map(friend => {
                const pending = friend.status === 'PENDING';
                return (
                  <div key={friend.friendUserId} style={styles.friendRow}>
                    <div style={styles.friendIdentity}>
                      <div style={styles.friendAvatar}>{(friend.username || '?')[0].toUpperCase()}</div>
                      <div>
                        <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{friend.username}</div>
                        <div style={{ color: friend.isOnline ? '#36d399' : '#777', fontSize: 11 }}>
                          {friend.isOnline ? '🟢 Online' : '⚫ Offline'}
                        </div>
                      </div>
                    </div>
                    {pending ? (
                      <button onClick={() => cancelInvitation(friend.invitationId)} disabled={!!inviteBusy} style={styles.pendingBtn}>⏳ Pending · Cancel</button>
                    ) : (
                      <button onClick={() => inviteFriend(friend)} disabled={!!inviteBusy} style={styles.inviteSmallBtn}>{inviteBusy === String(friend.friendUserId) ? 'Sending…' : 'Invite'}</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Cancel button */}
      <button style={styles.cancelBtn} onClick={handleCancelClick}>
        Cancel Room
      </button>
    </div>
    </>
  );
}
